import React from "react";

import { css, cx } from "@emotion/css";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { Images } from "@/assets/images.tsx";
import { useStyles } from "@/hooks/useStyles.ts";
import { Queries } from "@/queries/Queries.ts";
import type { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Dialog } from "@/uiKit/atoms/Dialog.tsx";
import { Tag } from "@/uiKit/atoms/Tag.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type LibreviewImportCardProps = {
  className?: string;
};

export const LibreviewImportCard: React.FC<LibreviewImportCardProps> = ({ className }) => {
  const styles = useStyles(makeStyles);
  const status = Queries.patientSync.useLibreViewStatus({});
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleLibreviewImportClick = () => {
    if (status.status === "ok") {
      void navigate({ to: "/patient-sync/libreview-select" });
    } else {
      setIsDialogOpen(true);
    }
  };

  return (
    <Card className={cx(styles.container, className)}>
      <CardHeader
        title={<Images.libreview alt="LibreView" className={styles.logo} />}
        action={status.status === "ok" ? <Tag color="green" label={t("patientSync.libreview.connected")} /> : null}
      />
      <CardContent className={styles.content}>
        <Typography variant="subtitle">{t("patientSync.libreview.title")}</Typography>
        <Typography variant="body">{t("patientSync.libreview.subtitle")}</Typography>
      </CardContent>
      <CardFooter position="start">
        <Button
          variant="contained"
          data-action="sync.libreview.start"
          endIcon={<Icons.chevronRight className={styles.icon} />}
          onClick={handleLibreviewImportClick}
        >
          {status.status === "ok"
            ? t("patientSync.libreview.importPatients")
            : t("patientSync.libreview.connectClinic")}
        </Button>
      </CardFooter>
      <LibreviewConnectionInfoDialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} />
    </Card>
  );
};

export type LibreviewConnectionInfoDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const LibreviewConnectionInfoDialog: React.FC<LibreviewConnectionInfoDialogProps> = ({ open, onClose }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();

  return (
    <Dialog id="libreview-connection-info-dialog" open={open} onClose={onClose} maxWidth="md">
      <CardHeader
        id="libreview-connection-info-header"
        title={t("patientSync.libreview.connectionTitle")}
        onExit={onClose}
        variant="contained"
      />
      <CardContent>
        <Typography variant="body" className={styles.dialogContent}>
          {t("patientSync.libreview.connectionBody")}
        </Typography>
      </CardContent>
      <CardFooter position="end">
        <Button variant="contained" data-action="sync.libreview.retry" onClick={() => window.location.reload()}>
          {t("patientSync.libreview.refresh")}
        </Button>
        <Button variant="outlined" data-action="sync.libreview.close" onClick={onClose}>
          {t("patientSync.libreview.close")}
        </Button>
      </CardFooter>
    </Dialog>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({}),
  content: css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
  }),
  logo: css({
    maxHeight: theme.spacing(32),
  }),
  icon: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
  dialogContent: css({
    whiteSpace: "pre-line",
  }),
});
